import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import Textarea from './Textarea';

const defaultStyle = {
  overflow: 'hidden',
  resize: 'none',
};

const AutoHeightTextarea = ({ value, defaultFieldValue, onBlur, onChange, ...rest }) => {
  const textareaRef = useRef();
  const [fieldValue, setFieldValue] = useState(value);

  const autosize = useCallback(
    debounce((ref) => {
      if (!ref.current) {
        return;
      }

      ref.current.style.height = '0px';
      const newHeight = ref.current.scrollHeight;
      ref.current.style.height = `${newHeight}px`;

      // add a scrollbar when reached max height and stopped resizing
      if (ref.current.clientHeight < newHeight) {
        ref.current.style.overflow = 'auto';
      } else {
        ref.current.style.overflow = 'hidden';
      }
    }, 250),
    [],
  );

  useEffect(() => {
    const onResize = () => autosize(textareaRef);
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [autosize, textareaRef]);

  useEffect(() => setFieldValue(value), [value]);

  useEffect(() => autosize(textareaRef), [fieldValue, textareaRef, autosize]);

  const handleChange = (e) => {
    setFieldValue(e.target.value);

    if (onChange) {
      onChange(e);
    }
  };

  const handleBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }

    if (defaultFieldValue && !fieldValue) {
      setFieldValue(defaultFieldValue);
    }
  };

  return (
    <Textarea
      style={defaultStyle}
      ref={textareaRef}
      value={fieldValue}
      onChange={handleChange}
      onBlur={handleBlur}
      {...rest}
    />
  );
};

AutoHeightTextarea.propTypes = {
  value: PropTypes.string,
  defaultFieldValue: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

AutoHeightTextarea.defaultProps = {
  value: '',
  defaultFieldValue: null,
  onChange: null,
  onBlur: null,
};

export default AutoHeightTextarea;
